import { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Tooltip, OverlayTrigger } from "react-bootstrap";
import { faEdit, faUserCircle } from "@fortawesome/free-solid-svg-icons";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import Loader from "../loader/loader";
import {
  getCustomerById,
  getWebsitesForTabs
} from "../../services/api-declaration";
import { TwentyPixelFontDiv } from "../common-styling";
import { showError } from "../../services/toast-service";
import NestedTabsComponent from "../nested-tabs-component";
import { ACCESS_CONFIG, CUSTOMER_ROUTES, NESTED_SIDENAV } from "../strings";
import { useTranslation } from "react-i18next";
import { CustomerWebsiteTabsContext } from "../../context/customer-website-tabs-context";
import { loadTargetTabRoutes } from "../../services/website-legal-pages";

const CustomerView = () => {
  const { t } = useTranslation();
  const { MARKETING_MANAGER_TIER } = ACCESS_CONFIG;
  const params = useParams();
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [websiteTabs, setWebsiteTabs] = useState([]);
  const tabRoutesRequestRef = useRef(0);
  const isHubspotTabSelected = state?.isHubspotTabSelected;
  const isWebsiteRoute = pathname.includes("/website");

  const COCKPIT_TABS = [
    {
      evKey: CUSTOMER_ROUTES.OVERVIEW,
      title: t("overview"),
      access: MARKETING_MANAGER_TIER
    },
    {
      evKey: CUSTOMER_ROUTES.FUNNELS,
      title: t("common.funnel", { count: 2 }),
      access: MARKETING_MANAGER_TIER
    },
    {
      evKey: CUSTOMER_ROUTES.LEADS,
      title: t("common.lead", { count: 2 }),
      access: MARKETING_MANAGER_TIER
    },
    {
      evKey: CUSTOMER_ROUTES.PERFORMANCE_REPORT,
      title: t("common.performanceReport", { count: 2 }),
      access: MARKETING_MANAGER_TIER
    },
    {
      evKey: CUSTOMER_ROUTES.INVOICES,
      title: t("common.invoices", { count: 2 }),
      access: MARKETING_MANAGER_TIER
    },
    {
      evKey: CUSTOMER_ROUTES.WEBSITE,
      title: t("common.website", { count: 2 }),
      access: MARKETING_MANAGER_TIER,
      hasDynamicNestedTabs: Boolean(websiteTabs.length)
    }
  ];

  const HUBSPOT_TABS = [
    {
      evKey: CUSTOMER_ROUTES.WEBSITE,
      title: t("common.website", { count: 2 }),
      access: MARKETING_MANAGER_TIER
    }
  ];

  const getCustomer = async () => {
    try {
      const response = await getCustomerById(params.id, isHubspotTabSelected);
      setCustomer(response.data.data);
    } catch (error) {
      console.log(error);
      showError(
        error.response.data.message ||
          t("toastMessage.errorInGetting", {
            what: t("common.customer")
          })
      );
    } finally {
      setIsLoading(false);
    }
  };
  const customerCb = useCallback(
    getCustomer,
    // eslint-disable-next-line
    [params.id, isHubspotTabSelected]
  );
  useEffect(() => {
    customerCb();
  }, [customerCb]);

  // Latest answer only: the view stays mounted when the customer changes
  const fetchWebsitesForTabs = () =>
    loadTargetTabRoutes({
      customerId: params.id,
      requestRef: tabRoutesRequestRef,
      fetchTabRoutes: getWebsitesForTabs,
      onLoaded: (tabRoutes) =>
        setWebsiteTabs(
          tabRoutes.map((_tabRoute) => ({
            evKey: _tabRoute,
            title: _tabRoute,
            access: MARKETING_MANAGER_TIER
          }))
        ),
      onFailed: (e) => {
        console.log(e);
        setWebsiteTabs([]);
        // e.g. 404 for a customer of another workspace (Asana 1212190375734539)
        showError(
          t("toastMessage.errorInGetting", {
            what: t("common.website", { count: 2 })
          })
        );
      }
    });

  useEffect(() => {
    if (isWebsiteRoute) fetchWebsitesForTabs();
    // eslint-disable-next-line
  }, [params.id, isWebsiteRoute]);

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <div className="d-flex h-100">
        <CustomerWebsiteTabsContext.Provider
          value={{ websiteTabs, setWebsiteTabs }}
        >
          <div className="sub-sidebar">
            <div className="inner-sub-sidebar">
              <div className="tab-heading ps-3 mt-4">
                {t("sidebar.customers")}
              </div>
              <NestedTabsComponent
                clearParams
                nestedNavType={NESTED_SIDENAV.CUSTOMER}
                tabs={isHubspotTabSelected ? HUBSPOT_TABS : COCKPIT_TABS}
              />
              {/* Website nested sidebar */}
              {isWebsiteRoute && (
                <div className="mt-1 ms-4">
                  <NestedTabsComponent
                    nestedNavType={NESTED_SIDENAV.WEBSITE}
                    tabs={websiteTabs}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="outlet-wrapper">
            <div className="mt-4">
              <TwentyPixelFontDiv className="fw-bold d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faUserCircle} className="me-2" />
                  {customer.firstName} {customer.lastName}
                </div>
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip>
                      {t("common.customer")} {t("common.edit")}
                    </Tooltip>
                  }
                >
                  <div className="ms-3">
                    <Button
                      variant="outline-dark"
                      onClick={() => navigate(`edit`)}
                    >
                      <FontAwesomeIcon icon={faEdit} className="me-2" />
                      {t("common.edit")}
                    </Button>
                  </div>
                </OverlayTrigger>
              </TwentyPixelFontDiv>
            </div>
            <div>
              <Outlet />
            </div>
          </div>
        </CustomerWebsiteTabsContext.Provider>
      </div>
    </>
  );
};
export default CustomerView;
